/**
 * UserService.js
 * 
 * Provides user-related services such as fetching, searching, and updating user data.
 */

// Firebase
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Services
import { patchNode } from 'services/nodes/NodeWriteService';

/**
 * Determines if a user with the email already exists.
 *
 * @param {string} email - User email to check for.
 * 
 * @returns {Promise<boolean>} Whether the email exists.
 */
export async function emailExists(email) {
    const user = await getUserWithEmail(email);
    return user !== null;
}

/**
 * Combines auth user info with Firebase user node data.
 *
 * @param {object} authUser - Firebase Auth user object.
 * 
 * @returns {Promise<Object|null>} Combined user object or null if not found.
 */
export async function fetchCurrentUser(authUser) {

    // Validate input
    if (!authUser) return null;

    try {
        const userData = await getUser(authUser.uid);
        if (userData) {
            return {
                key: authUser.uid,
                email: authUser.email,
                displayName: authUser.displayName,
                emailVerified: authUser.emailVerified,
                phoneNumber: authUser.phoneNumber,
                userKey: authUser.uid,
                ...userData
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error combining user data:", error);
        throw error;
    }
}

/**
 * Fetches a user node by key.
 *
 * @param {string} key - Key of the user node to retrieve.
 * 
 * @returns {Promise<Object|null>} A promise that resolves to the user object if found, or null if not found.
 */
export async function getUser(key) {
    try {
        const ref = doc(db, collections.nodes, key);
        const docSnap = await getDoc(ref);

        if (docSnap.exists()) {
            const data = docSnap.data() || {};
            if (data?.key && data.key !== docSnap.id) {
                console.warn(`[UserService] getUser key mismatch for doc ${docSnap.id}: stored key=${data.key}`);
            }
            return { id: docSnap.id, ...data, key: docSnap.id };
        } else {
            return null;
        }
    } catch (error) {
        if (String(error?.code || '').includes('permission-denied')) {
            console.warn('[UserService] getUser permission denied (transient during auth/bootstrap?):', {
                key,
                code: error?.code,
            });
        } else {
            console.error("Error fetching user node:", error);
        }
        throw error;
    }
}

/**
 * Subscribes to updates for a user node document.
 * 
 * @param {string} userKey - Key of the user node to subscribe to.
 * @param {(user: Object|null) => void} onUpdate - Callback invoked on data updates.
 * @param {(err: any) => void} [onError] - Optional callback invoked on errors.
 * 
 * @returns {() => void} unsubscribe
 */
export function subscribeToUserNode(userKey, onUpdate, onError) {
    if (!userKey) {
        throw new Error('subscribeToUserNode: userKey is required');
    }
    if (typeof onUpdate !== 'function') {
        throw new Error('subscribeToUserNode: onUpdate callback is required');
    }

    const ref = doc(db, collections.nodes, userKey);
    return onSnapshot(
        ref,
        (snap) => {
            if (!snap.exists()) {
                onUpdate(null);
                return;
            }
            const data = snap.data() || {};
            if (data?.key && data.key !== snap.id) {
                console.warn(`[UserService] subscribeToUserNode key mismatch for doc ${snap.id}: stored key=${data.key}`);
            }
            onUpdate({ id: snap.id, ...data, key: snap.id });
        },
        (err) => {
            if (typeof onError === 'function') onError(err);
            else console.warn('[UserService] subscribeToUserNode error:', err);
        }
    );
}

/**
 * Determines if a user with the email already exists. Returns the user
 * if true, or null if no user with the email exists.
 *
 * @param {string} email - User email to check for.
 * 
 * @returns {Promise<Object|null>} A promise that resolves to the user object if found, or null if not found.
 */
export async function getUserWithEmail(email) {

    try {
        // Query users collection for matching email
        const usersDatabase = collection(db, collections.nodes);

        // Build query
        const q = query(
            usersDatabase,
            where('type', '==', 'USER'),
            where("data_profile.email", "==", email)
        );

        // Execute query
        const querySnapshot = await getDocs(q);

        // Return first matching user or null
        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const data = docSnap.data() || {};
            if (data?.key && data.key !== docSnap.id) {
                console.warn(`[UserService] getUserWithEmail key mismatch for doc ${docSnap.id}: stored key=${data.key}`);
            }
            return { id: docSnap.id, ...data, key: docSnap.id };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching user:", error);
        throw error;
    }
}

/**
 * Determines if a user with the phone number already exists. Returns the user
 * if true, or null if no user with the phone exists.
 *
 * @param {string} phone - User phone to check for.
 * 
 * @returns {Promise<Object|null>} A promise that resolves to the user object if found, or null if not found.
 */
export async function getUserWithPhone(phone) {

    try {
        // Query users collection for matching phone
        const usersDatabase = collection(db, collections.nodes);

        // Build query
        const q = query(
            usersDatabase,
            where('type', '==', 'USER'),
            where("data_profile.phone", "==", phone)
        );

        // Execute query
        const querySnapshot = await getDocs(q);

        // Return first matching user or null
        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const data = docSnap.data() || {};
            if (data?.key && data.key !== docSnap.id) {
                console.warn(`[UserService] getUserWithPhone key mismatch for doc ${docSnap.id}: stored key=${data.key}`);
            }
            return { id: docSnap.id, ...data, key: docSnap.id };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching user:", error);
        throw error;
    }
}

/**
 * Searches for users by indexed profile text.
 *
 * @param {string} searchTerm - Query string to search for in the user index.
 * 
 * @returns {Promise<Array>} Filtered array of objects matching the query.
 */
export async function search(searchTerm) {

    // Validate input
    if (!searchTerm) return [];

    // Perform search
    try {
        const q = query(
            collection(db, collections.nodes),
            where('type', '==', 'USER'),
            where('data_profile.index', 'array-contains', searchTerm.toLowerCase()),
            limit(50)
        );

        // Execute query
        const querySnapshot = await getDocs(q);
        
        // Map results to data objects
        return querySnapshot.docs.map(doc => doc.data());
        
    } catch (error) {
        console.error('Error searching users:', error);
        throw error;
    }
}

/**
 * Updates a user's information.
 *
 * @param {string} userKey - Key of the object being updated.
 * @param {string} data - Data to update.
 */
export async function update(userKey, data) {
    try {
        await patchNode(userKey, data);
    } catch (error) {
        console.error("Error updating current user: ", error);
        throw error;
    }
}
